import React from 'react';

import { Link } from 'react-router-dom';

import { DashboardOutlined, EnvironmentOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { ErpOrderStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { SearchPurchaseOrderDTO } from '@recurrency/core-api-schema/dist/search/getSearchPurchaseOrders';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { ColumnType } from 'antd/lib/table';

import { ButtonLink } from 'components/Links';
import { ErpOrderStatusBadge } from 'components/recipes/ErpOrderStatusBadge';
import { NewPurchaseOrderButton } from 'components/recipes/NewPurchaseOrderButton';
import { SearchFrame } from 'components/recipes/SearchFrame';
import { ValueFacet } from 'components/recipes/SearchFrame/types';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { formatDateShorthand, toTitleCase } from 'utils/formatting';
import { routes } from 'utils/routes';
import { asKeyOf, sortableDollarColumn, sortableNumberColumn } from 'utils/tables';
import { isTenantErpTypeP21 } from 'utils/tenants';

export const PurchaseOrderListPage = () => {
  const { activeTenant } = useGlobalApp();

  const columns: (ColumnType<SearchPurchaseOrderDTO> | null)[] = [
    {
      title: 'PO No',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('purchase_order_number'),
      render: (purchaseOrderNumber: string) => (
        <Link to={routes.purchasing.purchaseOrderDetails(purchaseOrderNumber)}>{purchaseOrderNumber}</Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('status'),
      // Unlike sales orders and quotes, typesense currently returns these as lowercase ErpOrderStatus values
      render: (status: ErpOrderStatus) => <ErpOrderStatusBadge erpOrderStatus={status} />,
    },
    {
      title: 'Order Date',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('order_date'),
      render: (orderDate: string) => formatDateShorthand(orderDate),
      sorter: true,
    },
    {
      title: 'Expected Date',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('expected_date'),
      render: (expectedDate: string | null) => formatDateShorthand(expectedDate),
    },
    {
      title: 'Buyer',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('buyer_desc'),
    },
    {
      title: 'Supplier',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('supplier_desc'),
    },
    sortableDollarColumn({
      title: 'Total Amount',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('purchase_value'),
    }),
    sortableNumberColumn({ title: 'Line Count', dataIndex: asKeyOf<SearchPurchaseOrderDTO>('line_count') }),
    {
      title: '',
      dataIndex: asKeyOf<SearchPurchaseOrderDTO>('purchase_order_number'),
      render: (purchaseOrderNumber: string) => ButtonLink(routes.purchasing.purchaseOrderDetails(purchaseOrderNumber)),
    },
  ];

  const valueFacets: (ValueFacet<SearchPurchaseOrderDTO> | null)[] = [
    {
      title: 'Status',
      field: 'status',
      icon: <DashboardOutlined />,
      labelFormatFn: toTitleCase,
    },
    {
      title: 'Buyer',
      field: 'buyer_desc',
      queryPlaceholder: 'Search buyers',
      icon: <UserOutlined />,
    },
    {
      title: 'Location',
      field: 'location_desc',
      queryPlaceholder: 'Search locations',
      icon: <EnvironmentOutlined />,
    },
    {
      title: 'Supplier',
      field: 'supplier_desc',
      queryPlaceholder: 'Search suppliers',
      icon: <ShopOutlined />,
    },
    isTenantErpTypeP21(activeTenant.erpType)
      ? {
          title: 'Vendor',
          field: 'vendor_desc',
          queryPlaceholder: 'Search vendors',
          icon: <ShopOutlined />,
        }
      : null,
  ];

  return (
    <SearchFrame<SearchPurchaseOrderDTO>
      title="Purchase Orders"
      indexName={SearchIndexName.PurchaseOrders}
      queryPlaceholder="Search recent purchase orders"
      columns={columns.filter(truthy)}
      valueFacets={valueFacets.filter(truthy)}
      headerActions={<NewPurchaseOrderButton />}
    />
  );
};
